import { setUpTOTP, updateMFAPreference } from "aws-amplify/auth";
import { RectangleEllipsis } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useGetAuthUser } from "@/api/get-user";
import { useUpdateUser } from "@/features/account-settings/api/update-user";
import { OnboardingHeader } from "@/features/registration/components/onboarding-header";
import { Select2FAForm } from "@/features/registration/components/select-2fa.form";
import { SMS2FAForm } from "@/features/registration/components/sms-2fa.form";
import { Totp2FAForm } from "@/features/registration/components/totp-2fa.form";
import { Select2FAFormData } from "@/features/registration/types/select-2fa.schema";
import { queryClient } from "@/lib/react-query";

export function SetupTotp() {
  const navigate = useNavigate();
  const { state } = useLocation();

  const [selected, setSelected] = useState<Select2FAFormData["type"] | null>(
    null,
  );
  const [secret, setSecret] = useState<string | null>(null);
  const [setupUri, setSetupUri] = useState<string | null>(null);

  const { data: authUser } = useGetAuthUser();
  const { mutateAsync: updateUser } = useUpdateUser({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
  });

  useEffect(() => {
    const fetchSetup = async () => {
      const totpSetupDetails = await setUpTOTP();
      const appName = "ClassView";
      const setupUri = totpSetupDetails.getSetupUri(appName);
      setupUri.pathname = setupUri.pathname.replace(/%22/g, "");

      setSecret(totpSetupDetails.sharedSecret);
      setSetupUri(setupUri.toString());
    };

    fetchSetup();
  }, []);

  const handleSMSSuccess = async () => {
    await updateMFAPreference({ sms: "PREFERRED" });

    if (authUser?.sub) {
      await updateUser({
        userId: authUser.sub,
        user: {
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      });
    }

    navigate(state?.redirectUrl || "/");
  };

  const handleTotpSuccess = async () => {
    await updateMFAPreference({ totp: "PREFERRED" });

    if (authUser?.sub) {
      await updateUser({
        userId: authUser.sub,
        user: {
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      });
    }

    navigate(state?.redirectUrl || "/");
  };

  const handleEmailSuccess = async () => {
    await updateMFAPreference({ email: "PREFERRED" });

    if (authUser?.sub) {
      await updateUser({
        userId: authUser.sub,
        user: {
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      });
    }

    navigate(state?.redirectUrl || "/");
  };

  /**
   * If email 2fa selected, no need to verify it again
   * @param type
   */
  const handleSubmit = async (type: Select2FAFormData["type"]) => {
    if (type === "email") {
      await handleEmailSuccess();
      return;
    }

    setSelected(type);
  };

  return (
    <>
      <OnboardingHeader
        title="Setup 2FA"
        description="Configure 2-factor authentication"
        Icon={RectangleEllipsis}
        className="mb-6"
      />
      <p className="mb-4 text-secondary">
        In order to access this system, we require you to enable two-factor
        authentication. Select your preferred authentication method.
      </p>

      {!selected && <Select2FAForm onSubmit={handleSubmit} />}
      {selected === "totp" && secret && setupUri && (
        <Totp2FAForm
          secret={secret}
          setupUri={setupUri}
          goBack={() => setSelected(null)}
          onSuccess={handleTotpSuccess}
        />
      )}
      {selected === "sms" && (
        <SMS2FAForm
          goBack={() => setSelected(null)}
          onSuccess={handleSMSSuccess}
        />
      )}
    </>
  );
}
